/**
 * LensSwitch — the top-level Company ⇄ You toggle (Home Redesign, 2026-06-02).
 *
 * Segmented control in the header, OWNER/ADMIN ONLY (useRole().hasMinRole('admin')).
 * Company = teal pill → `/` (the entity's money). You = violet pill → `/me` (the
 * person's money across all their entities + outside income). This is what makes
 * the no-commingling rule structural — two lenses, one switch.
 *
 * Active lens is derived from the URL (`/me*` ⇒ You) so it's deep-linkable +
 * back-button-correct. The choice is also recorded in localStorage (parity with
 * the nav-layout pattern) for a future default; we never auto-redirect on it.
 *
 * Compact by design so it fits the 375px header alongside the entity switcher.
 */
import { useLocation, useNavigate } from 'react-router-dom';
import { Building2, CircleUser } from 'lucide-react';
import { useRole } from '@/hooks/useRole';
import { lensForPath, lensRoute, writeLens, type Lens } from '@/lib/personal-overview';

export function LensSwitch() {
  const { hasMinRole } = useRole();
  const location = useLocation();
  const navigate = useNavigate();

  // Owner/admin only — staff never see the personal lens.
  if (!hasMinRole('admin')) return null;

  const active = lensForPath(location.pathname);

  const go = (lens: Lens) => {
    if (lens === active) return;
    writeLens(lens);
    navigate(lensRoute(lens));
  };

  return (
    <div
      role="group"
      aria-label="Company or personal view"
      data-testid="lens-switch"
      className="shrink-0 inline-flex items-center rounded-full bg-slate-100 dark:bg-slate-800 p-0.5 border border-slate-200 dark:border-slate-700"
    >
      <button
        type="button"
        onClick={() => go('company')}
        aria-pressed={active === 'company'}
        data-testid="lens-company"
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs sm:text-sm font-medium transition-colors ${
          active === 'company'
            ? 'bg-primary-600 text-white shadow-sm'
            : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
        }`}
      >
        <Building2 className="w-4 h-4" />
        <span className="hidden sm:inline">Company</span>
      </button>
      <button
        type="button"
        onClick={() => go('you')}
        aria-pressed={active === 'you'}
        data-testid="lens-you"
        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs sm:text-sm font-medium transition-colors ${
          active === 'you'
            ? 'bg-violet-600 text-white shadow-sm'
            : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
        }`}
      >
        <CircleUser className="w-4 h-4" />
        <span className="hidden sm:inline">You</span>
      </button>
    </div>
  );
}
